import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, message, Spin, Typography, Button, Space, Modal, Steps, Alert } from 'antd';
import {
    CheckCircleOutlined,
    PictureOutlined,
    ShoppingOutlined,
    TeamOutlined,
    RocketOutlined,
    InfoCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { updateAuctionStatus } from '../../redux/userSlice';
import auctionService from '../../services/auctionService';
import agentService from '../../services/agentService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import AuctionForm from './AuctionForm';
import PrivateAuctionModal from './PrivateAuctionModal';

const { Title, Text } = Typography;

const CreateEditAuction = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { slug } = useParams();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [agents, setAgents] = useState([]);
    const [isPrivate, setIsPrivate] = useState(null);
    const [biddingType, setBiddingType] = useState(null);
    const [hasAgents, setHasAgents] = useState(false);
    const [imageModalVisible, setImageModalVisible] = useState(false);
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const [newAuctionSlug, setNewAuctionSlug] = useState(null);
    const [auctionStatus, setAuctionStatus] = useState(null);
    const isEdit = !!slug;

    useEffect(() => {
        loadInitialData();
    }, [slug]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const promises = [auctionService.getCategories(), agentService.getAgents()];
            
            if (isEdit) {
                promises.push(auctionService.getAuction(slug));
            }

            const results = await Promise.all(promises);
            const categoriesResponse = results[0];
            const agentsResponse = results[1];
            const auctionResponse = isEdit ? results[2] : null;
            
            setCategories(categoriesResponse.data);
            
            if (agentsResponse && agentsResponse.agents) {
                setAgents(agentsResponse.agents);
            }

            if (isEdit && auctionResponse) {
                const auction = auctionResponse.data;
                
                console.log('Auction data:', auction);
                
                // Check if auction has agents assigned
                const hasAgentsAssigned = auction.agents && auction.agents.length > 0;
                const agentIds = hasAgentsAssigned ? auction.agents.map(agent => agent.id) : [];
                
                // Format the data for form fields
                const formData = {
                    title: auction.title,
                    description: auction.description,
                    terms_conditions: auction.terms_conditions,
                    category_id: auction.category?.id || auction.category_id,
                    location: auction.location,
                    is_private: Boolean(auction.is_private),
                    is_sequential: Boolean(auction.is_sequential),
                    start_time: moment(auction.start_time),
                    video_url: auction.video_url,
                    has_agents: hasAgentsAssigned,
                    agent_ids: agentIds,
                    lot_close_time_seconds: auction.lot_close_time_seconds || 60
                };
                
                console.log('Setting form data:', formData);
                form.setFieldsValue(formData);
                setIsPrivate(Boolean(auction.is_private));
                setBiddingType(Boolean(auction.is_sequential));
                setHasAgents(hasAgentsAssigned);
                setAuctionStatus(auction.status);
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
            message.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const auctionData = {
                ...values,
                start_time: values.start_time.toISOString(),
                category_id: values.category_id
            };

            // Handle agent_ids based on has_agents checkbox
            if (!values.has_agents) {
                // If has_agents is unchecked, send empty array to clear all agents
                auctionData.agent_ids = [];
            }
            // If has_agents is true, agent_ids will be included from values
            delete auctionData.has_agents; // Remove has_agents from submission data

            if (isEdit) {
                await auctionService.updateAuction(slug, auctionData);
                message.success('Auction updated successfully');
                navigate(`/auctions/${slug}/images`);
            } else {
                const response = await auctionService.createAuction(auctionData);
                setNewAuctionSlug(response.data.slug);
                // Update user's auction status in Redux store
                dispatch(updateAuctionStatus(true));
                setSuccessModalVisible(true);
            }
        } catch (error) {
            console.error('Error saving auction:', error);
            message.error(error.response?.data?.message || 'Failed to save auction');
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessModalClose = () => {
        setSuccessModalVisible(false);
        navigate(`/auctions/${newAuctionSlug}/images`);
    };

    const renderSuccessModal = () => {
        return (
            <Modal
                title={
                    <Space size="middle">
                        <div style={{
                            background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                            padding: '8px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <CheckCircleOutlined style={{ color: 'white', fontSize: '24px' }} />
                        </div>
                        <span style={{ fontSize: '20px', fontWeight: 600, color: '#1a1a1a' }}>
                            🎉 Auction Created Successfully!
                        </span>
                    </Space>
                }
                open={successModalVisible}
                onCancel={handleSuccessModalClose}
                footer={[
                    <Button 
                        key="continue" 
                        type="primary" 
                        onClick={handleSuccessModalClose}
                        size="large"
                        style={{ 
                            borderRadius: '8px',
                            minWidth: '200px',
                            height: '44px',
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        📸 Continue to Add Images
                    </Button>
                ]}
                width={700}
                style={{ borderRadius: '16px' }}
            >
                <div style={{ padding: '20px 0' }}>
                    <Typography.Paragraph style={{ fontSize: '16px', marginBottom: 28, color: '#595959', lineHeight: '1.6' }}>
                        <strong style={{ color: '#52c41a' }}>Congratulations!</strong> Your auction has been created successfully. 
                        Here's what you can do next to make it even better:
                    </Typography.Paragraph>

                    <Steps
                        direction="vertical"
                        current={0}
                        items={[
                            {
                                title: <span style={{ fontWeight: 500, fontSize: '16px' }}>Add Auction Images</span>,
                                description: (
                                    <span style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                        Upload high-quality images of your auction items. This helps attract more bidders and makes your auction stand out.
                                    </span>
                                ),
                                icon: <PictureOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                            },
                            {
                                title: <span style={{ fontWeight: 500, fontSize: '16px' }}>Create Lots</span>,
                                description: (
                                    <span style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                        Add individual items or groups of items to your auction. Each lot can have its own specifications and pricing.
                                    </span>
                                ),
                                icon: <ShoppingOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                            },
                            {
                                title: <span style={{ fontWeight: 500, fontSize: '16px' }}>Add Lot Images</span>,
                                description: (
                                    <span style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                        Upload images for each lot in your auction. Make sure to include clear, detailed photos of each item.
                                    </span>
                                ),
                                icon: <PictureOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                            }
                        ]}
                        style={{ marginBottom: 24 }}
                    />

                    <div style={{ 
                        marginTop: 24, 
                        padding: 20, 
                        backgroundColor: '#f6ffed', 
                        border: '2px solid #b7eb8f',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(82, 196, 26, 0.1)'
                    }}>
                        <Space size="middle" align="start">
                            <InfoCircleOutlined style={{ color: '#52c41a', fontSize: '20px', marginTop: 2 }} />
                            <Typography.Text style={{ fontSize: '15px', color: '#595959', lineHeight: '1.6' }}>
                                <strong style={{ color: '#389e0d' }}>Ready to add images?</strong> Click "Continue to Add Images" 
                                to start uploading photos that showcase your auction in the best light!
                            </Typography.Text>
                        </Space>
                    </div>
                </div>
            </Modal>
        );
    };

    return (
        <DashboardLayout className="create-edit-auction">
            <Spin spinning={loading && isEdit}>
                <div style={{ marginBottom: 32 }}>
                    <div style={{ marginBottom: 16 }}>
                        <Title level={2} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                            {isEdit ? '✏️ Edit Auction' : '✨ Create New Auction'}
                        </Title>
                        <Typography.Paragraph style={{ color: '#595959', fontSize: '16px', marginTop: 8, marginBottom: 0 }}>
                            {isEdit 
                                ? 'Update your auction details and settings. Make any changes you need below.'
                                : 'Welcome! Let\'s create an amazing auction together. Fill in the details below, and you can add images and lots right after.'}
                        </Typography.Paragraph>
                    </div>
                    
                    {/* Completed Auction Alert */}
                    {isEdit && auctionStatus === 'completed' && (
                        <Alert
                            message={<span style={{ fontWeight: 500 }}>Auction Completed</span>}
                            description="This auction has already been completed. You cannot update completed auctions."
                            type="warning"
                            showIcon
                            style={{ 
                                marginTop: 16,
                                borderRadius: '8px',
                                border: '2px solid #ffe58f'
                            }}
                        />
                    )}
                    
                    {/* Cancelled Auction Alert */}
                    {isEdit && auctionStatus === 'cancelled' && (
                        <Alert
                            message={<span style={{ fontWeight: 500 }}>Auction Cancelled</span>}
                            description="This auction has been cancelled. You cannot update cancelled auctions."
                            type="warning"
                            showIcon
                            style={{ 
                                marginTop: 16,
                                borderRadius: '8px',
                                border: '2px solid #ffe58f'
                            }}
                        />
                    )}
                    
                    {/* Payment Alert for Scheduled Auctions */}
                    {isEdit && auctionStatus === 'scheduled' && (
                        <Alert
                            message={<span style={{ fontWeight: 500, fontSize: '16px' }}>📋 Scheduled Auction - Payment Required</span>}
                            description={
                                <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                    <p style={{ marginBottom: 12 }}>Your auction is currently scheduled and will not appear in live auctions until payment is completed.</p>
                                    <p style={{ marginBottom: 8, fontWeight: 500 }}><strong>To activate your auction:</strong></p>
                                    <ul style={{ margin: '8px 0', paddingLeft: '24px', lineHeight: '1.8' }}>
                                        <li>Contact the admin to complete payment processing</li>
                                        <li>Provide auction details and payment information</li>
                                        <li>Once payment is confirmed, your auction will be activated</li>
                                    </ul>
                                    <div style={{ 
                                        marginTop: 12, 
                                        padding: '12px 16px', 
                                        background: '#fff7e6', 
                                        borderRadius: '8px',
                                        border: '1px solid #ffd591'
                                    }}>
                                        <Text strong style={{ color: '#fa8c16', fontSize: '14px' }}>
                                            ⚠️ Your auction is not visible to bidders until activated
                                        </Text>
                                    </div>
                                </div>
                            }
                            type="warning"
                            showIcon
                            icon={<ExclamationCircleOutlined />}
                            style={{ 
                                marginTop: 16,
                                marginBottom: 16,
                                borderRadius: '12px',
                                border: '2px solid #ffe58f'
                            }}
                            action={
                                <Button size="large" type="primary" style={{ borderRadius: '8px' }}>
                                    Contact Admin
                                </Button>
                            }
                        />
                    )}
                </div>
                <AuctionForm
                    form={form}
                    loading={loading}
                    isEdit={isEdit}
                    categories={categories}
                    agents={agents}
                    isPrivate={isPrivate}
                    setIsPrivate={setIsPrivate}
                    biddingType={biddingType}
                    setBiddingType={setBiddingType}
                    hasAgents={hasAgents}
                    setHasAgents={setHasAgents}
                    onImageModalClick={() => setImageModalVisible(true)}
                    onSubmit={handleSubmit}
                    disabled={isEdit && (auctionStatus === 'completed' || auctionStatus === 'cancelled')}
                />
                <div style={{ 
                    marginTop: 40,
                    paddingTop: 32,
                    borderTop: '2px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 16
                }}>
                    <div>
                        <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                            {isEdit 
                                ? '💡 Make your changes and click "Update Auction" when you\'re ready.'
                                : '💡 Review your details and click "Create Auction" when everything looks good!'}
                        </Typography.Text>
                    </div>
                    <Space size="middle">
                        <Button 
                            onClick={() => window.history.back()}
                            size="large"
                            style={{ borderRadius: '8px', minWidth: '100px' }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            onClick={() => form.submit()} 
                            loading={loading}
                            disabled={isEdit && (auctionStatus === 'completed' || auctionStatus === 'cancelled')}
                            size="large"
                            style={{ 
                                borderRadius: '8px',
                                minWidth: '160px',
                                height: '40px',
                                fontWeight: 500,
                                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                            }}
                        >
                            {loading ? 'Processing...' : (isEdit ? '✅ Update Auction' : '✨ Create Auction')}
                        </Button>
                    </Space>
                </div>
            </Spin>

            <PrivateAuctionModal
                visible={imageModalVisible}
                onCancel={() => setImageModalVisible(false)}
            />

            {renderSuccessModal()}
        </DashboardLayout>
    );
};

export default CreateEditAuction; 